/**
 * Makes the two DOM operations React uses to unmount and reorder children
 * survive a third party having moved the nodes out from under it.
 *
 * <p>The problem is not ours to fix at the source: browser translators (Google
 * Translate above all), Grammarly, password managers and a good share of ad
 * blockers all rewrite the live DOM. Translate is the worst of them — it
 * replaces text nodes with its own {@code <font>} wrappers, lazily, as content
 * scrolls into view. React kept a reference to the original node, and when it
 * later unmounts that subtree it calls {@code parent.removeChild(node)} for a
 * node the parent no longer owns. The DOM throws
 * {@code NotFoundError: Failed to execute 'removeChild' on 'Node'}, React
 * cannot recover mid-commit, and the entire page is replaced by the error
 * boundary — for what is, in truth, a cosmetic disagreement about who owns a
 * text node.</p>
 *
 * <p>So: when the node genuinely is not where the caller thinks, warn and carry
 * on instead of throwing. A misplaced node stays on screen or fails to move —
 * visible, minor, survivable. The alternative is a dead page.</p>
 *
 * <p>This is a safety net, not a licence. The {@code console.warn} is
 * deliberate: it keeps the churn visible so a real reconciler bug in our own
 * code still shows up in the console rather than being silently swallowed.
 * {@code translate="no"} on the app interior (see {@code AppShell}) is the
 * actual fix for the reported crash; this catches everything we did not
 * predict.</p>
 */

let installed = false;

export function installDomMutationGuard() {
  // Idempotent: a second install would wrap the wrapper, and Vite's HMR
  // re-executes module bodies on edit.
  if (installed) return;
  installed = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChild<T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      // eslint-disable-next-line no-console
      console.warn(
        "[dom-guard] removeChild called for a node that is not a child of this parent — " +
          "ignoring (a browser extension or translator most likely re-parented it).",
        { child, parent: this },
      );
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBefore<T extends Node>(
    this: Node,
    node: T,
    reference: Node | null,
  ): T {
    if (reference && reference.parentNode !== this) {
      // eslint-disable-next-line no-console
      console.warn(
        "[dom-guard] insertBefore called with a reference node that is not a child of this " +
          "parent — appending instead (a browser extension or translator most likely " +
          "re-parented it).",
        { node, reference, parent: this },
      );
      return originalInsertBefore.call(this, node, null) as T;
    }
    return originalInsertBefore.call(this, node, reference) as T;
  };
}
